/**
 * How much of the screen the agent has taken, and where it all sits.
 *
 * THREE MODES, NOT FOUR. The designs named `rail` and `sheet` separately,
 * but they are the same decision read at two breakpoints. Collapsing them
 * into `panel` means the agent asks for an AMOUNT of screen and the layout
 * decides the shape, which is one fewer thing for a model to get wrong.
 *
 * ONE CENTRED STACK, and the orb never moves. The first attempt positioned
 * the panel top-right and the orb bottom-right independently, and on
 * screen they read as two unrelated bits of chrome that happened to appear
 * at the same time. The orb is the thing somebody is talking to, so
 * everything the agent puts up belongs directly above it in the same
 * column, and the orb itself lives in the composer rather than floating
 * free. One object.
 *
 * NOTHING IS INERT BY ACCIDENT. The layer is `pointer-events-none` and
 * every painted surface turns it back on. Get that backwards and the agent
 * silently swallows every click on the page behind it, which is the
 * failure the whole design exists to avoid.
 *
 * ONLY `stage` DIMS. A backdrop says "stop what you are doing", and the
 * point of the other modes is that you do not have to.
 */

export type Occupancy = 'orb' | 'panel' | 'stage' | 'full' | 'canvas'

/**
 * Above everything, including dialogs.
 *
 * The uncomfortable half of the design and the right one: the agent is
 * often talking somebody THROUGH a dialog, and a layer that hid behind one
 * would vanish exactly when it was useful. It is only safe because the
 * layer is inert.
 *
 * 100_000 because this codebase already contains `z-[99999]` on a profile
 * gallery, a tag selector and two booking-detail overlays. Named here so
 * the next person raising a z-index has somewhere to look rather than
 * adding another nine.
 */
export const AGENT_Z = 100_000

/** The dim, one below the surface so the stack sits on its own backdrop. */
export const AGENT_Z_BACKDROP = AGENT_Z - 1

/** The inert container. Everything else lives inside it. */
export const LAYER_CLASSES = 'pointer-events-none fixed inset-0'

/**
 * The stack: panel, caption and composer, centred at the bottom.
 *
 * Capped at the prose width rather than the viewport. A composer stretched
 * across a 2560px monitor is one nobody can read the start and the end of,
 * and the options above it would be a row of letterboxes.
 */
export const STACK_CLASSES = [
  'pointer-events-none fixed inset-x-0 bottom-6 mx-auto',
  'flex flex-col items-stretch gap-3 px-4',
  'pb-[env(safe-area-inset-bottom)]',
].join(' ')

/**
 * How wide the stack gets.
 *
 * `xl` for a question, wider once it is a conversation with files in it,
 * because a transcript at 36rem with an image in it is a column of
 * thumbnails. Still capped: full bleed across a 2560px monitor is a
 * composer nobody can read both ends of.
 */
export function stackWidth(mode: Occupancy, panels = 0): string {
  // A diagram wants the room. Anything narrower and a six-box flow is a
  // column of boxes you have to pan, which is not a diagram.
  if (mode === 'canvas') return 'max-w-[92rem]'
  if (panels >= 3) return 'max-w-[84rem]'
  if (panels === 2) return 'max-w-6xl'
  if (panels === 1 || mode === 'full') return 'max-w-3xl'
  return 'max-w-xl'
}

/**
 * The row the instruments sit in.
 *
 * A ROW, NOT A GRID, and side by side rather than stacked, because the
 * whole appeal of this is density without a page load: everything relevant
 * in view, nothing behind a tab. Under `md` it becomes one column, because
 * three columns on a phone is three unreadable columns.
 *
 * `items-stretch` and a fixed height rather than letting each panel size
 * itself: three panels of three different heights over a live page reads
 * as clutter, and one tall panel would push the composer off the screen.
 */
export const PANEL_ROW_CLASSES = [
  'pointer-events-none flex min-h-0 w-full gap-3',
  'h-[clamp(14rem,52vh,34rem)]',
  'flex-col md:flex-row md:items-stretch',
].join(' ')

/**
 * The frosted surface, in ONE place.
 *
 * Shared by the panel, the composer and the caption because they are
 * three faces of one object and three copies would drift. Matching the
 * mobile pill's own `bg-background/90 backdrop-blur-xl` rather than
 * inventing a treatment.
 *
 * It floats over a page that is still live underneath, so some sense of
 * what is behind it is honest. What is NOT honest is bare text: the
 * caption shipped without this and over a dense bookings table it was
 * unreadable, which for the one part that exists to be read in a noisy
 * room is the whole job failed.
 */
export const GLASS = [
  // The mobile nav pill's own language, which is the most finished
  // surface in the product: a hairline that works on both themes, real
  // blur, and a shadow. The first version used `border-border` and no
  // shadow and read as a flat rectangle sat on the page rather than
  // something floating over it.
  'rounded-2xl border border-black/10 dark:border-white/10',
  'bg-[var(--background)]/85 backdrop-blur-xl',
  'shadow-2xl shadow-black/20',
].join(' ')

/** Does this mode stop the page being used? Only one does. */
export function blocksPage(mode: Occupancy): boolean {
  // `full` dims as well: it is the screen, deliberately, and somebody
  // reading a conversation back is not also using the page behind it.
  return mode === 'stage' || mode === 'full' || mode === 'canvas'
}

/**
 * The panel the agent fills: options, a figure, something to read.
 *
 * FROSTED, matching the mobile pill's own `bg-background/90
 * backdrop-blur-xl` rather than inventing a treatment. It floats over a
 * page that is still live underneath, so some sense of what is behind it
 * is honest; an opaque card would claim to be part of the page.
 *
 * `stage` is the SAME panel with more room and a backdrop. It does not
 * move or change shape, because somebody already reading it should not
 * have to find it again.
 */
export function panelClasses(mode: Occupancy): string {
  if (mode === 'orb') return 'hidden'
  if (mode === 'canvas') {
    /**
     * The canvas needs a real height and no padding.
     *
     * Excalidraw measures its own container and draws to fill it, so a
     * parent that sizes to content gives it zero height and a blank
     * canvas, and padding inside shows as a gap between the chrome and
     * the drawing surface.
     */
    return `pointer-events-auto flex flex-col overflow-hidden h-[76vh] ${GLASS}`
  }
  const base = `pointer-events-auto overflow-y-auto p-4 ${GLASS}`
  if (mode === 'full') {
    /**
     * FULL: the panel runs up the screen and its bottom edge goes BEHIND
     * the composer, which floats over it centrally.
     *
     * The same join as the mobile pill and its sheet, and for the same
     * reason: two rectangles with a gap between them are two things, and
     * one overlapping the other is one thing with a handle on it. The
     * bottom padding is what stops the last message hiding underneath.
     */
    return `${base} h-[72vh] pb-14`
  }
  return `${base} ${mode === 'stage' ? 'max-h-[78vh]' : 'max-h-[48vh]'}`
}

/**
 * The composer tucked up over the panel's bottom edge.
 *
 * A negative margin, which the house style usually forbids on a child.
 * The alternative is positioning the composer absolutely inside the
 * stack, which means the panel can no longer size itself from its own
 * content. One margin with a reason beats two layouts.
 */
export function composerOverlap(mode: Occupancy): string {
  return mode === 'full' ? 'relative z-10 -mt-7' : ''
}

/**
 * The caption: the last thing it said.
 *
 * `self-center` so it is only as wide as the words. A full-width band of
 * glass for seven words reads as a status bar, and this is meant to be
 * somebody's sentence.
 */
export const CAPTION_CLASSES = [
  // A CONTROL NOW, not a label. It was `pointer-events-none` on the
  // grounds that it was only a caption, but the last thing the agent said
  // is also the obvious place to press to read the rest of it.
  'pointer-events-auto self-center px-3 py-1.5 text-left',
  'text-sm text-foreground/80 hover:text-foreground',
  'transition-opacity duration-500',
  GLASS,
].join(' ')

/**
 * How long the last thing it said stays up.
 *
 * It used to stay forever, so a greeting sat over somebody's bookings
 * list until they pressed something. Long enough to read two lines at a
 * glance, short enough that the page is theirs again shortly after. The
 * conversation keeps it either way: this is a glance, not the record.
 */
export const CAPTION_LINGER_MS = 12_000

/**
 * The composer: type or paste, with the orb on the end.
 *
 * ALWAYS THERE while the surface is open, which is a change from the
 * designs, where typing was a part the agent had to offer. Nobody should
 * need an agent's permission to type, and "here is my old config" is
 * something somebody decides to do, not something they wait to be asked.
 */
export const COMPOSER_CLASSES = [
  'pointer-events-auto flex flex-col gap-1.5 p-2',
  GLASS,
].join(' ')

/**
 * The instrument buttons get their own row above the input.
 *
 * Inline with the textarea they squeezed it: five named buttons and a
 * paperclip and an orb left "Ask, paste, or drop a file in…" wrapping
 * onto two lines in a panel barely wider than the words. A row of its own
 * costs 28px and gives the input the full width back.
 */
export const PANEL_BAR_CLASSES = [
  'flex min-w-0 items-center gap-0.5 overflow-x-auto',
  'border-b border-black/5 pb-1.5 dark:border-white/5',
].join(' ')

/**
 * How much the agent asked for.
 *
 * Kept even though the panel now caps itself per mode, because the agent
 * still states how much room it wants and the rule that it may RAISE and
 * never lower still holds.
 */
/**
 * The target painted while a file is being dragged over the page.
 *
 * FULL SCREEN, because the listener is on the window and the honest
 * affordance for "drop it anywhere" is a target that covers everywhere.
 * A small rectangle by the composer would be a lie: the drop works
 * wherever you let go.
 *
 * `pointer-events-none` even though it is the drop target. The window
 * holds the listener; this is paint only, and taking events here would
 * put a dead layer over the page for the length of the drag.
 */
export const DROP_TARGET_CLASSES = [
  'pointer-events-none fixed inset-4 flex items-center justify-center',
  'rounded-xl border-2 border-dashed',
  'border-[var(--brand-hot,#ff2d87)] bg-background/60 backdrop-blur-sm',
].join(' ')

export type SheetHeight = 'peek' | 'half' | 'full'

/** The agent may only ever ask for more, never less. */
export function raiseOnly(current: SheetHeight, wanted: SheetHeight): SheetHeight {
  const order: SheetHeight[] = ['peek', 'half', 'full']
  return order.indexOf(wanted) > order.indexOf(current) ? wanted : current
}
